import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import Loading from "../../common/components/Loading";
import Box from "@mui/material/Box/Box";
import ExcessGroupFormContainer from "./ExcessGroupForm/ExcessGroupFormContainer";
import ExcessGroupFilters from "./Filters/ExcessGroupFilters";
import Grid from "@mui/material/Grid/Grid";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import {fromSingleGenericBeToSingleGenericFe, useActionColDef} from "../../utils";
import DialogComponent from "../../common/components/DialogComponent";
import {useRecoilState} from "recoil";
import {
    excessGroupAtom,
    excessGroupCreationAtom,
    excessGroupFiltersAtom,
    initialExcessGroupCreation,
} from "../../state/excessGroupsRecoil";
import {useContext, useEffect, useRef, useState} from "react";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {useAgGridLocale} from "../../../../config/globalConfig";
import {AgGridReact} from "ag-grid-react";
import {useExcessGroupRepository} from "../../api/repositories/ExcessGroupRepository";
import {useExcessGroupDetailRepository} from "../../api/repositories/ExcessGroupDetailRepository";
import {DialogInterface, SelectElementModelBE, SelectElementModelFE,} from "../../api/types/generic";
import {CommercialCategorySelectFE} from "../../api/types/commercialCategory";
import {
    ExcessGroupEntityRequest,
    ExcessGroupEntityResponse,
    FormExcessGroupModel,
    FormSearchExcessGroupModel,
} from "../../api/types/excessGroup";
import {ColDef, ICellRendererParams} from "ag-grid-community";
import {excessGroupColDef} from "./excessGroupColDef";
import ActionsPopover from "../../common/components/ActionsPopover";
import {DEFAULT_COL_DEF, SET_SNACKBAR} from "../../constants";
import BasicToolbar from "../../common/components/BasicToolbar";
import dayjs from "dayjs";
import {ExcessGroupDetail} from "../../api/types/excessGroupDetail";
import useAgGridCustomHook from "../../hooks/useAgGridCustomHook";

const ExcessGroup = () => {

    // Use Recoil
    const [excessGroupsFilters, setExcessGroupsFilters] = useRecoilState(excessGroupFiltersAtom);
    const [creationForm, setCreationForm] = useRecoilState(excessGroupCreationAtom);
    const [, setExcessGroup] = useRecoilState<ExcessGroupEntityResponse | null>(excessGroupAtom);

    // Use State
    const [excessGroupInUsed, setExcessGroupInUsed] = useState<boolean>(false)

    // Use Context
    const {dispatchGlobalData, globalData: {user}} = useContext(GlobalContext);

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    // Use Repository Hooks
    const {
        deleteExcessGroup,
        isExcessGroupInUse,
        getExcessGroupsPaged,
        terminateExcessGroup
    } = useExcessGroupRepository();
    const {getExcessGroupMatrix} = useExcessGroupDetailRepository();

    // Use State
    const [dialog, setDialog] = useState<DialogInterface | null>(null);
    const [isCreationFormOpen, setIsCreationFormOpen] = useState(false);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    const [testFilters, setTestFilters] = useState<FormSearchExcessGroupModel>({
        name: "",
        commercialCategoryId: null,
        commercialSegmentId: null,
        validityDateFrom: "",
        validityDateTo: "",
        dealers: dealerOption || [],
        legalEntities: legalEntityOptions
    })

    // Use Grid Data Custom Hook
    const {setServerSideDatasource, handleTriggerFilters, refresh} =
        useAgGridCustomHook<ExcessGroupEntityResponse, FormSearchExcessGroupModel>({
            gridRef,
            api: getExcessGroupsPaged,
            initialSortColId: "name",
            entityFilters: {
                name: "",
                commercialCategoryId: "",
                commercialSegmentId: "",
                validityDateFrom: "",
                validityDateTo: "",
                dealers: dealerFilteredOptions,
                legalEntities: everyLegalEntitiesWithoutAll
            } as FormSearchExcessGroupModel,
            customSort: "ExcessGroupEntityResponse",
        });

    const {actionColDef} = useActionColDef();
    const columnDefs: ColDef<ExcessGroupEntityResponse>[] = excessGroupColDef({
        actionsRenderer: (params: ICellRendererParams) => (
            <ActionsPopover
                params={params}
                view={{
                    action: viewButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
                        return profile.auths.find((auth: any) => {
                            return auth.name === "SCOPE_PRC_VIEW_DETAIL_EXCESS_GROUP"
                        })
                    })
                }}
                update={{
                    action: updateButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
                        return profile.auths.find((auth: any) => {
                            return auth.name === "SCOPE_PRC_UPDATE_EXCESS_GROUP"
                        })
                    })
                }}
                del={{
                    action: deleteButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
                        return profile.auths.find((auth: any) => {
                            return auth.name === "SCOPE_PRC_DELETE_EXCESS_GROUP"
                        })
                    })
                }}
                terminate={{
                    action: terminateButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
                        return profile.auths.find((auth: any) => {
                            return auth.name === "SCOPE_PRC_TERMINATE_EXCESS_GROUP"
                        })
                    })
                }}
            />
        ),
        actionColDef,
        user
    });

    const defaultColDef = DEFAULT_COL_DEF;

    const toolbar = {
        renderActions: () => (
            <BasicToolbar addIcon={
                !!user.profiles.find((profile: any) => {
                    return profile.auths.find((auth: any) => {
                        return auth.name === "SCOPE_PRC_CREATE_EXCESS_GROUP";
                    });
                }) ?
                    {onClick: createButtonClicked} : undefined
            }/>
        )
    };

    const createButtonClicked = async () => {
        setCreationForm({
            readOnly: false,
            inUse: false,
            definitionData: undefined,
            step: 0,
            completed: [false, false, false]
        });
        setIsCreationFormOpen(true);
    };

    const getDefinitionData = (
        data: ExcessGroupEntityRequest
    ): FormExcessGroupModel => ({
        ...data,
        commercialCategory:
            (fromSingleGenericBeToSingleGenericFe(
                data.commercialCategory
            ) as CommercialCategorySelectFE) || null,
        commercialSegmentId: fromSingleGenericBeToSingleGenericFe(
            data.commercialCategory.commercialSegment
        ),
        dealer: {code: data.dealer, label: data.dealer},
        legalEntity: {code: data.legalEntity, label: data.legalEntity},
    });

    const updateButtonClicked = async (data: ExcessGroupEntityResponse) => {
        const excessGroupIsInUse = await isExcessGroupInUse(data.id);
        setExcessGroupInUsed(excessGroupIsInUse);
        const definitionData = getDefinitionData(data);
        const matrixData = await excessGroupMatrix(data.id);

        setCreationForm({
            readOnly: false,
            inUse: excessGroupIsInUse,
            groupId: data.id,
            definitionData: definitionData,
            matrixData,
            step: 0,
            completed: [!!definitionData, !!matrixData?.length, !!matrixData?.length]
        });
        setIsCreationFormOpen(true);
        setExcessGroup(data)
    };

    const deleteButtonClicked = (data: ExcessGroupEntityResponse) => {
        setDialog({
            open: true,
            onClickConfirm: () => handleConfirmDelete(data.id),
            message: t("pricing:excessGroup.dialogContents.removeExcessGroupInfo"),
        });
    };

    const handleConfirmDelete = async (id: number) => {
        try {
            if (id) {
                await deleteExcessGroup(id);
                setDialog(null);
                await handleGetExcessGroupsPage();
                dispatchGlobalData(SET_SNACKBAR.SUCCESS);
            }
        } catch (e) {
        }
    };

    const terminateButtonClicked = async (data: ExcessGroupEntityResponse) => {
        setDialog({
            open: true,
            onClickConfirm: () => handleConfirmTerminate(data.id),
            message: t("pricing:excessGroup.dialogContents.terminateExcessGroupInfo"),
        });
    };

    const handleConfirmTerminate = async (id: number) => {
        await terminateExcessGroup(id);
        setDialog(null);
        refresh();
        dispatchGlobalData(SET_SNACKBAR.SUCCESS);
    };

    const excessGroupMatrix = async (
        groupId: number
    ): Promise<ExcessGroupDetail[]> => {
        let out: ExcessGroupDetail[] = [];
        try {
            out = await getExcessGroupMatrix(groupId);
        } catch (e) {
        }
        return out;
    };

    const viewButtonClicked = async (data: ExcessGroupEntityResponse) => {
        setCreationForm({
            readOnly: true,
            inUse: false,
            groupId: data.id,
            definitionData: getDefinitionData(data),
            matrixData: await excessGroupMatrix(data.id),
            step: 0,
            completed: [true, true, true]
        });
        setIsCreationFormOpen(true);
    };

    const handleGetExcessGroupsPage = async () => {
        gridRef.current!.api.refreshServerSide({purge: true});
    };

    useEffect(() => {
        if (gridRef?.current) {
            handleTriggerFilters({
                name: testFilters.name,
                commercialSegmentId:
                    (
                        testFilters.commercialSegmentId as SelectElementModelFE
                    )?.code?.toString() || "",
                commercialCategoryId:
                    (
                        testFilters.commercialCategoryId as SelectElementModelFE
                    )?.code?.toString() || "",
                validityDateFrom: testFilters.validityDateFrom
                    ? dayjs(testFilters.validityDateFrom).toISOString()
                    : testFilters.validityDateFrom,
                validityDateTo: testFilters.validityDateTo
                    ? dayjs(testFilters.validityDateTo).toISOString()
                    : testFilters.validityDateTo,
                legalEntities: Array.isArray(testFilters.legalEntities) 
                    ? (testFilters.legalEntities as SelectElementModelFE[]).map(el => el.code as string)
                    : [],
                dealers: Array.isArray(testFilters.dealers)
                    ? (testFilters.dealers as SelectElementModelFE[]).map(el => el.code as string)
                    : [],
            });
        }
    }, []);

    useEffect(() => {
        handleTriggerResetFilters()
        // eslint-disable-next-line
    }, [])

    const handleTriggerResetFilters = () => {
        setTestFilters({
            name: "",
            commercialCategoryId: null,
            commercialSegmentId: null,
            validityDateFrom: "",
            validityDateTo: "",
            dealers: dealerOption || [],
            legalEntities: legalEntityOptions
        });
        handleTriggerFilters({
            name: "",
            commercialCategoryId: "",
            commercialSegmentId: "",
            validityDateFrom: "",
            validityDateTo: "",
            dealers: dealerFilteredOptions,
            legalEntities: legalEntityOptions?.map((le: SelectElementModelFE) => le.code as string) || []
        });
    };

    const handleClickSearch = () =>
        handleTriggerFilters({
            name: testFilters.name,
            commercialSegmentId:
                (
                    testFilters.commercialSegmentId as SelectElementModelFE
                )?.code?.toString() || "",
            commercialCategoryId:
                (
                    testFilters.commercialCategoryId as SelectElementModelFE
                )?.code?.toString() || "",
            validityDateFrom: testFilters.validityDateFrom
                ? dayjs(testFilters.validityDateFrom).toISOString()
                : testFilters.validityDateFrom,
            validityDateTo: testFilters.validityDateTo
                ? dayjs(testFilters.validityDateTo).toISOString()
                : testFilters.validityDateTo,
            legalEntities: Array.isArray(testFilters.legalEntities) 
                ? (testFilters.legalEntities as SelectElementModelFE[]).map(el => el.code as string)
                : [],
            dealers: Array.isArray(testFilters.dealers)
                ? (testFilters.dealers as SelectElementModelFE[]).map(el => el.code as string)
                : [],
        });

    // useEffect(() => {
    //     setTimeout(() => {
    //         handleClickSearch();
    //     }, 500);
    // }, [i18n.resolvedLanguage]);

    const onDone = () => {
        setIsCreationFormOpen(false);
        setExcessGroup(null);
        setCreationForm(initialExcessGroupCreation);
        refresh();
        dispatchGlobalData(SET_SNACKBAR.SUCCESS);
    };

    const onClose = () => {
        creationForm.readOnly || refresh();
        setIsCreationFormOpen(false);
        setExcessGroup(null);
        setCreationForm(initialExcessGroupCreation);
    };

    return (
        <Box>
            <SectionHeader title={t("translation:auths.SCOPE_PRC_VIEW_EXCESS_GROUP.name")}/>
            <ExcessGroupFormContainer
                onDone={onDone}
                isOpen={isCreationFormOpen}
                onClose={onClose}
                refresh={refresh}
                excessGroupInUsed={excessGroupInUsed}
            />
            <ExcessGroupFilters
                handleClickSearch={handleClickSearch}
                handleClickResetSearch={handleTriggerResetFilters}
                testFilters={testFilters}
                setTestFilters={setTestFilters}
            />
            <Grid container spacing={2} p={2}>
                <Grid item xs={12} sm={12}>
                    <AgGridCustomTable
                        gridRef={gridRef}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        tooltipShowDelay={0}
                        toolbar={toolbar}
                        pagination
                        localeText={agGridLocale}
                        domLayout="autoHeight"
                        rowModelType="serverSide"
                        paginationPageSize={10}
                        cacheBlockSize={10}
                        serverSideInfiniteScroll
                        onGridReady={setServerSideDatasource}
                    />
                </Grid>
            </Grid>
            {dialog?.open && (
                <DialogComponent
                    type={"info"}
                    onClickConfirm={dialog.onClickConfirm}
                    setOpen={() => setDialog(null)}
                    open={dialog.open}
                    message={dialog.message}
                />
            )}
            <Loading/>
        </Box>
    );
};

export default ExcessGroup;
